/**
 * Currency Context
 * Provides currency state and conversion utilities throughout the app
 * 
 * Currency selection logic:
 * 1. Anonymous users: Currency is set based on browser locale/timezone detection
 * 2. Logged-in users: Currency is set from user's preferred_currency setting
 * 3. Manual selection: User can change currency anytime (saved to localStorage + user profile)
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { apiGet } from '../lib/api';

// Currency configuration (matches backend)
const CURRENCY_CONFIG = {
  SEK: { symbol: 'kr', position: 'after', decimals: 0, name: 'Swedish Krona', locale: 'sv-SE' },
  DKK: { symbol: 'kr', position: 'after', decimals: 0, name: 'Danish Krone', locale: 'da-DK' },
  NOK: { symbol: 'kr', position: 'after', decimals: 0, name: 'Norwegian Krone', locale: 'nb-NO' },
  EUR: { symbol: '€', position: 'before', decimals: 2, name: 'Euro', locale: 'de-DE' },
  ISK: { symbol: 'kr', position: 'after', decimals: 0, name: 'Icelandic Króna', locale: 'is-IS' },
  USD: { symbol: '$', position: 'before', decimals: 2, name: 'US Dollar', locale: 'en-US' },
};

// Country to currency mapping
const COUNTRY_CURRENCY_MAP = {
  SE: 'SEK', DK: 'DKK', NO: 'NOK', FI: 'EUR', IS: 'ISK',
  DE: 'EUR', FR: 'EUR', NL: 'EUR', BE: 'EUR', AT: 'EUR',
  IE: 'EUR', ES: 'EUR', IT: 'EUR', PT: 'EUR',
  US: 'USD',
};

// Supported currencies list (Nordic focused)
const SUPPORTED_CURRENCIES = ['SEK', 'DKK', 'NOK', 'EUR', 'ISK', 'USD'];

// Default currency
const DEFAULT_CURRENCY = 'SEK';

// Local storage key
const CURRENCY_STORAGE_KEY = 'kreatives:currency';

const CurrencyContext = createContext(null);

/**
 * Detect user's country from browser/timezone
 */
function detectCountry() {
  try {
    // Try to get from timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Map common Nordic timezones
    if (timezone.includes('Stockholm') || timezone.includes('Sweden')) return 'SE';
    if (timezone.includes('Copenhagen') || timezone.includes('Denmark')) return 'DK';
    if (timezone.includes('Oslo') || timezone.includes('Norway')) return 'NO';
    if (timezone.includes('Helsinki') || timezone.includes('Finland')) return 'FI';
    if (timezone.includes('Reykjavik') || timezone.includes('Iceland')) return 'IS';
    if (timezone.includes('Berlin') || timezone.includes('Germany')) return 'DE';
    if (timezone.includes('Paris') || timezone.includes('France')) return 'FR';
    if (timezone.includes('Amsterdam') || timezone.includes('Netherlands')) return 'NL';
    
    // Try to get from navigator.language
    const lang = navigator.language || navigator.userLanguage;
    if (lang) {
      const parts = lang.split('-');
      if (parts.length >= 2) {
        const country = parts[1].toUpperCase();
        if (COUNTRY_CURRENCY_MAP[country]) return country;
      }
    }
    
    return null;
  } catch (e) {
    return null;
  }
}

/**
 * Get currency for a country code
 */
function getCurrencyForCountry(countryCode) {
  if (!countryCode) return DEFAULT_CURRENCY;
  return COUNTRY_CURRENCY_MAP[countryCode.toUpperCase()] || 'USD';
}

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(DEFAULT_CURRENCY);
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Fetch exchange rates (extracted for reuse)
  const fetchRates = useCallback(async () => {
    try {
      const res = await apiGet('/exchange-rates');
      if (res?.success && res?.data?.rates) {
        const rateMap = {};
        for (const r of res.data.rates) {
          rateMap[`${r.from_currency}-${r.to_currency}`] = parseFloat(r.rate);
        }
        setRates(rateMap);
      }
    } catch (err) {
      console.warn('[Currency] Failed to fetch rates, using defaults');
    }
  }, []);

  // Fetch user's preferred currency when logged in
  const fetchUserCurrency = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth:token');
      if (!token) return null;
      
      const res = await apiGet('/users/me');
      const user = res?.data?.user || res?.user || res?.data || null;
      return user?.preferred_currency || null;
    } catch (err) {
      console.warn('[Currency] Failed to fetch user preference');
      return null;
    }
  }, []);

  // Load currency preference and rates on mount
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      
      try {
        // 1. Check if user is logged in
        const token = localStorage.getItem('auth:token');
        setIsLoggedIn(!!token);
        
        // 2. Check localStorage for saved preference
        const saved = localStorage.getItem(CURRENCY_STORAGE_KEY);
        
        // 3. Detect country for anonymous users
        const detected = detectCountry();
        setDetectedCountry(detected);
        
        // 4. Determine currency priority:
        //    a) If logged in, try to get from user's preferred_currency
        //    b) If localStorage has value, use it
        //    c) Use detected locale
        //    d) Fall back to default
        let selectedCurrency = DEFAULT_CURRENCY;
        
        if (token) {
          // User is logged in - try to fetch their preference
          const userCurrency = await fetchUserCurrency();
          if (userCurrency && SUPPORTED_CURRENCIES.includes(userCurrency)) {
            selectedCurrency = userCurrency;
            // Sync to localStorage
            localStorage.setItem(CURRENCY_STORAGE_KEY, userCurrency);
          } else if (saved && SUPPORTED_CURRENCIES.includes(saved)) {
            selectedCurrency = saved;
          } else if (detected) {
            selectedCurrency = getCurrencyForCountry(detected);
          }
        } else {
          // Anonymous user
          if (saved && SUPPORTED_CURRENCIES.includes(saved)) {
            selectedCurrency = saved;
          } else if (detected) {
            selectedCurrency = getCurrencyForCountry(detected);
            // Save detected currency to localStorage for consistency
            localStorage.setItem(CURRENCY_STORAGE_KEY, selectedCurrency);
          }
        }
        
        setCurrencyState(selectedCurrency);
        
        // 5. Fetch exchange rates
        await fetchRates();
      } catch (err) {
        console.error('[Currency] Init error:', err);
      } finally {
        setLoading(false);
      }
    };
    
    init();
  }, [fetchRates, fetchUserCurrency]);

  // Listen for auth changes (login/logout) to update currency
  useEffect(() => {
    const handleAuthChange = async (e) => {
      // Check for auth:token or auth:user changes
      if (e?.key === 'auth:token' || e?.key === 'auth:user') {
        const token = localStorage.getItem('auth:token');
        const wasLoggedIn = isLoggedIn;
        const nowLoggedIn = !!token;
        
        setIsLoggedIn(nowLoggedIn);
        
        if (nowLoggedIn && !wasLoggedIn) {
          // User just logged in - fetch their preferred currency
          const userCurrency = await fetchUserCurrency();
          if (userCurrency && SUPPORTED_CURRENCIES.includes(userCurrency)) {
            setCurrencyState(userCurrency);
            localStorage.setItem(CURRENCY_STORAGE_KEY, userCurrency);
          }
        } else if (!nowLoggedIn && wasLoggedIn) {
          // User logged out - keep current currency (don't reset)
          // This provides continuity for the user
        }
      }
    };
    
    window.addEventListener('storage', handleAuthChange);
    return () => window.removeEventListener('storage', handleAuthChange);
  }, [isLoggedIn, fetchUserCurrency]);

  // Set currency (saves to localStorage)
  const setCurrency = useCallback((newCurrency) => {
    if (!SUPPORTED_CURRENCIES.includes(newCurrency)) {
      console.warn(`[Currency] Unsupported currency: ${newCurrency}`);
      return;
    }
    setCurrencyState(newCurrency);
    localStorage.setItem(CURRENCY_STORAGE_KEY, newCurrency);
  }, []);

  // Update currency from user preference (called from Settings page after save)
  const updateFromUserPreference = useCallback((newCurrency) => {
    if (!newCurrency || !SUPPORTED_CURRENCIES.includes(newCurrency)) {
      return;
    }
    setCurrencyState(newCurrency);
    localStorage.setItem(CURRENCY_STORAGE_KEY, newCurrency);
  }, []);

  // Convert amount from one currency to another
  const convert = useCallback((amount, fromCurrency = 'SEK', toCurrency = currency) => {
    if (!amount || isNaN(amount)) return 0;
    if (fromCurrency === toCurrency) return amount;
    
    const rate = rates[`${fromCurrency}-${toCurrency}`];
    if (!rate) {
      // No rate found, return original amount
      return amount;
    }
    
    const converted = amount * rate;
    const config = CURRENCY_CONFIG[toCurrency] || { decimals: 2 };
    
    // Round based on currency decimals
    return config.decimals === 0 
      ? Math.round(converted)
      : Math.round(converted * 100) / 100;
  }, [rates, currency]);

  // Format price with currency symbol
  const formatPrice = useCallback((amount, currencyOverride = null, options = {}) => {
    const curr = currencyOverride || currency;
    const config = CURRENCY_CONFIG[curr] || CURRENCY_CONFIG.USD;
    
    // Convert if source currency is different
    let displayAmount = amount;
    if (options.fromCurrency && options.fromCurrency !== curr) {
      displayAmount = convert(amount, options.fromCurrency, curr);
    }
    
    // Format number
    const formatted = new Intl.NumberFormat(config.locale, {
      minimumFractionDigits: config.decimals,
      maximumFractionDigits: config.decimals,
    }).format(displayAmount);
    
    // Apply symbol position
    if (config.position === 'before') {
      return `${config.symbol}${formatted}`;
    } else {
      return `${formatted} ${config.symbol}`;
    }
  }, [currency, convert]);

  // Format price from SEK (most prices are stored in SEK)
  const formatFromSEK = useCallback((amountInSEK) => {
    return formatPrice(amountInSEK, null, { fromCurrency: 'SEK' });
  }, [formatPrice]);

  // Get current currency config
  const currencyConfig = useMemo(() => {
    return CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.USD;
  }, [currency]);

  const value = {
    // Current currency state
    currency,
    setCurrency,
    currencyConfig,
    detectedCountry,
    loading,
    isLoggedIn,
    
    // Method for Settings page to update from user preference
    updateFromUserPreference,
    
    // Conversion utilities
    convert,
    formatPrice,
    formatFromSEK,
    rates,
    
    // Static data
    SUPPORTED_CURRENCIES,
    CURRENCY_CONFIG,
    COUNTRY_CURRENCY_MAP,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

/**
 * Hook to use currency context
 */
export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}

/**
 * Format price utility (standalone, for use outside context)
 */
export function formatPriceStatic(amount, currency = 'SEK') {
  const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.USD;
  
  const formatted = new Intl.NumberFormat(config.locale, {
    minimumFractionDigits: config.decimals,
    maximumFractionDigits: config.decimals,
  }).format(amount);
  
  if (config.position === 'before') {
    return `${config.symbol}${formatted}`;
  } else {
    return `${formatted} ${config.symbol}`;
  }
}

export default CurrencyContext;
